import { drawing as d } from '@progress/kendo-drawing';
import { Rect } from '../math';
import { defined, isFunction } from '../utils';
import { removeChildren } from './utils';
/**
 * A canvas that provides a drawing surface for rendering visual elements.
 * Manages the underlying drawing surface and provides methods for drawing operations.
 */
export class Canvas {
    /**
     * Creates a new Canvas instance.
     * @param element The HTML element to attach the canvas to
     * @param options Configuration options for the canvas
     */
    constructor(element, options) {
        /** @hidden */
        this._translate = (x, y) => {
            const viewBox = this._viewBox;
            if (defined(x) && defined(y)) {
                viewBox.x = x;
                viewBox.y = y;
                this.surface.translate({ x: x, y: y });
            }
            return {
                x: viewBox.x,
                y: viewBox.y
            };
        };
        this.element = element;
        this.surface = d.Surface.create(element, options);
        if (isFunction(this.surface.translate)) {
            this.translate = this._translate;
        }
        this.drawingElement = new d.Group();
        this._viewBox = new Rect(0, 0, options.width, options.height);
        this.size(this._viewBox);
    }
    /**
     * Gets the bounding box of all drawing elements on the canvas.
     * @returns A rectangle representing the bounds of the canvas content
     */
    bounds() {
        const box = this.drawingElement.clippedBBox();
        return new Rect(0, 0, box.width(), box.height());
    }
    /**
     * Gets or sets the size of the canvas.
     * @param size The size to set, or undefined to get current size
     * @returns The current size when called as getter
     */
    size(size) {
        const viewBox = this._viewBox;
        if (defined(size)) {
            viewBox.width = size.width;
            viewBox.height = size.height;
            this.surface.setSize(size);
        }
        return {
            width: viewBox.width,
            height: viewBox.height
        };
    }
    /**
     * Draws all visual elements to the canvas surface.
     */
    draw() {
        this.surface.draw(this.drawingElement);
    }
    /**
     * Appends a visual element to the canvas.
     * @param visual The visual element to append
     * @returns The canvas instance for method chaining
     */
    append(visual) {
        this.drawingElement.append(visual.drawingContainer());
        return this;
    }
    /**
     * Removes a visual element from the canvas.
     * @param visual The visual element to remove
     */
    remove(visual) {
        this.drawingElement.remove(visual.drawingContainer());
    }
    /**
     * Inserts a visual element before another element (currently not implemented).
     */
    insertBefore() { }
    /**
     * Clears all visual elements from the canvas.
     */
    clear() {
        this.drawingElement.clear();
    }
    /**
     * Destroys the canvas and optionally removes the HTML element.
     * @param clearHtml Whether to remove the HTML element from the DOM
     */
    destroy(clearHtml) {
        this.surface.destroy();
        if (clearHtml) {
            removeChildren(this.element);
            this.element.remove();
        }
    }
}
